import {Delta} from 'quill';

import {isNil} from 'ramda';

import {RichTextTemplateTokens} from '../types';
import {splitTemplateTokenText} from './splitTemplateTokenText';
import {TEMPLATE_TOKEN} from './TEMPLATE_TOKEN';

const findChangedRange = (change: Delta) => {
  let position = 0;
  let start: number | null = null;
  let end = 0;

  change.ops.forEach((op) => {
    if (typeof op.retain === 'number') {
      position += op.retain;
      return;
    }
    if (!isNil(op.insert)) {
      start ??= position;
      position += typeof op.insert === 'string' ? op.insert.length : 1;
      end = position;
      return;
    }
    start ??= position;
    end = Math.max(end, position);
  });

  return isNil(start) ? null : {start, end};
};

/**
 * The change that turns known placeholders typed or pasted as plain text into tokens. Only
 * placeholders that touch the user's change are converted, so literal text elsewhere stays.
 */
export function buildTypedTemplateTokenConversion(
  change: Delta,
  contents: Delta,
  getLabel: RichTextTemplateTokens['getLabel']
) {
  const changedRange = findChangedRange(change);

  if (isNil(changedRange)) {
    return null;
  }

  const conversion = new Delta();
  let position = 0;
  let consumed = 0;

  contents.ops.forEach((op) => {
    if (typeof op.insert !== 'string') {
      position += 1;
      return;
    }

    splitTemplateTokenText(op.insert, getLabel).forEach((part) => {
      if (part.type !== 'token') {
        return;
      }
      const start = position + part.index;
      const end = start + part.length;

      if (start > changedRange.end || end < changedRange.start) {
        return;
      }
      conversion
        .retain(start - consumed)
        .delete(part.length)
        .insert({[TEMPLATE_TOKEN.blotName]: {code: part.code, label: part.label}}, op.attributes);
      consumed = end;
    });
    position += op.insert.length;
  });

  return consumed === 0 ? null : conversion;
}
